import { CommonModule } from '@angular/common';
import { Injector, ModuleWithProviders, NgModule, Type } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatRadioModule } from '@angular/material/radio';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PersistencyTypes } from '@common-modules/common-persistency/persistency-types';
import {
  DYNAMIC_LAYOUT_EXTERNAL_SETTINGS,
  DynamicLayoutExternalSettings,
} from '@common-modules/dynamic-layout/dynamic-layout-external-settings';
import { CommonSharedModule } from '@common-modules/shared/common-shared.module';
import { WidgetRegistryService } from '@common-modules/widget-registry/widget-registry';
import { TranslateModule } from '@ngx-translate/core';
import { DynamicLayoutModule } from '../dynamic-layout/dynamic-layout.module';
import { GenericStoreService } from '../dynamic-layout/state/generic/generic-store.service';
import { ReduxStateModule } from '../redux/redux-state.module';
import { ComponentRendererComponent } from '../shared/component-renderer/component-renderer.component';
import { LabelValueListModule } from '../shared/core/label-value-list/label-value-list.module';
import { IconModule } from '../shared/icon/icon.module';
import { CreateDashboardFormComponent } from './dashboards/create-dashboard-form/create-dashboard-form.component';
import { CreateDashboardPopupComponent } from './dashboards/create-dashboard-popup/create-dashboard-popup.component';
import { DashboardManagerService } from './dashboards/dashboard-manager.service';
import { DashboardWidgetComponent } from './dashboards/dashboard-widget/dashboard-widget.component';
import { DashboardsService } from './dashboards/dashboards.service';
import { DefaultDashboardsService } from './dashboards/default-dashboards.service';
import { GlDetailPanelComponent } from './tab-panel/gl-detail-panel/gl-detail-panel.component';

export class RightPanelModuleSettings {
  dashboardsService?: Type<DashboardsService>;
  dynamicLayoutSettings?: DynamicLayoutExternalSettings;
}

@NgModule({
  declarations: [
    GlDetailPanelComponent,
    CreateDashboardFormComponent,
    CreateDashboardPopupComponent,
    DashboardWidgetComponent,
  ],
  imports: [
    CommonModule,
    MatTabsModule,
    MatIconModule,
    MatTooltipModule,
    MatButtonModule,
    MatMenuModule,
    TranslateModule,
    MatButtonModule,
    DynamicLayoutModule.forFeature({
      persistency: PersistencyTypes.Local,
    }),
    ReduxStateModule.forFeature({
      storeServices: [GenericStoreService],
    }),
    LabelValueListModule,
    IconModule,
    ComponentRendererComponent,
    CommonSharedModule,
    // Dashboard dependencies
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatRadioModule,
  ],
  exports: [GlDetailPanelComponent],
  providers: [
    DefaultDashboardsService,
    {
      provide: DashboardsService,
      useExisting: DefaultDashboardsService,
    },
    DashboardManagerService,
  ],
})
export class RightPanelModule {
  static injector: Injector;
  static forFeature(
    settings: RightPanelModuleSettings,
  ): ModuleWithProviders<RightPanelModuleSettings> {
    const providers = [];

    if (settings.dashboardsService) {
      providers.push({
        provide: DashboardsService,
        useExisting: settings.dashboardsService,
      });
    }

    if (settings.dynamicLayoutSettings) {
      providers.push({
        provide: DYNAMIC_LAYOUT_EXTERNAL_SETTINGS,
        useValue: settings.dynamicLayoutSettings,
      });
    }

    return {
      ngModule: RightPanelModule,
      providers,
    };
  }

  constructor(injector: Injector, widgetRegistry: WidgetRegistryService) {
    RightPanelModule.injector = injector;
    widgetRegistry.register([
      {
        name: 'DashboardWidgetComponent',
        widgetClass: DashboardWidgetComponent,
      },
    ]);
  }
}
